import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { lcApiBase, lcHttpError } from './logiciel-caisse-api.helper';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';

export interface DashboardFilters {
  date_from: string;
  date_to: string;
  entrepot_id: number | null;
}

export interface DashboardVentes {
  kpis: { nb_documents: number; ca_ttc: number };
  series: { day: string; count: number; ca: number }[];
}

export interface DashboardStock {
  kpis: {
    qty_physique: number;
    qty_disponible: number;
    nb_references: number;
  };
}

export interface DashboardProduits {
  kpis: { total_catalogue: number; crees_periode: number };
  series: { day: string; created: number }[];
}

export interface DashboardTransferts {
  kpis: { count: number; quantite_total: number };
  series: { day: string; count: number; quantite: number }[];
}

export interface DashboardTicketsAnnules {
  kpis: { nb_documents: number; ca_ttc: number };
  series: { day: string; count: number; ca: number }[];
}

export interface DashboardClients {
  kpis: { total_clients: number; nouveaux_periode: number };
  series: { day: string; count: number }[];
}

export interface DashboardStatsPayload {
  success: boolean;
  filters: DashboardFilters;
  ventes: DashboardVentes;
  stock: DashboardStock;
  produits: DashboardProduits;
  transferts: DashboardTransferts;
  tickets_annules: DashboardTicketsAnnules;
  clients: DashboardClients;
  panier_moyen: number;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class LogicielCaisseDashboardStatsService {
  constructor(
    private readonly http: HttpClient,
    private readonly authService: LogicielCaisseAuthService
  ) {}

  fetchStats(params: {
    dateFrom: string;
    dateTo: string;
    entrepotId: number | 'all' | null;
  }): Observable<DashboardStatsPayload> {
    let hp = new HttpParams()
      .set('date_from', params.dateFrom)
      .set('date_to', params.dateTo);
    if (params.entrepotId != null && params.entrepotId !== 'all') {
      hp = hp.set('entrepot_id', String(params.entrepotId));
    }
    const url = `${lcApiBase(this.authService)}/api/dashboard/stats`;
    return this.http.get<DashboardStatsPayload>(url, { params: hp }).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Statistiques indisponibles');
        }
        return res;
      }),
      catchError((err) =>
        throwError(() =>
          lcHttpError(
            err as { error?: unknown; status?: number },
            'Erreur lors du chargement du tableau de bord'
          )
        )
      )
    );
  }
}

/** Remplit chaque jour entre deux dates ISO (YYYY-MM-DD) pour des courbes continues. */
export function enumerateDaysInclusive(from: string, to: string): string[] {
  const out: string[] = [];
  const a = new Date(from + 'T12:00:00');
  const b = new Date(to + 'T12:00:00');
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) {
    return out;
  }
  for (let d = new Date(a); d <= b; d.setDate(d.getDate() + 1)) {
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

export function shortDayLabel(iso: string): string {
  if (!iso || iso.length < 10) {
    return iso;
  }
  const [, m, day] = iso.split('-');
  return `${day}/${m}`;
}

/** Mappe une série journalière par clé `day` (YYYY-MM-DD). */
export function seriesByDay<T extends { day: string }>(series: T[]): Map<string, T> {
  return new Map(series.map((row) => [row.day, row]));
}

export function daySeriesValue(
  map: Map<string, { [key: string]: unknown }>,
  day: string,
  key: string
): number {
  const row = map.get(day);
  if (!row) {
    return 0;
  }
  const n = Number(row[key]);
  return Number.isFinite(n) ? n : 0;
}

export function formatTnd(value: number, fractionDigits = 2): string {
  return `${value.toLocaleString('fr-TN', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })} TND`;
}
